import {
    Condition,
    Conditions,
    ConditionTypes,
    Operators,
} from "@tenforce/product-fields"
import { ConditionDTO } from "../../models/dto/conditions"
import { selectorModelMapper } from "../../treeSelector/utils"
import { Type, TypeModel } from "../../models/treeSelector/models"
import { ValueWithGroup } from "../../treeSelector/types"
import {
    SelectorConditionDTO,
    SelectorConditionDTOType,
} from "../../models/dto/conditions/SelectorCondition"
import { FilterParser } from "."

export const OptionConditionDTOType = "#TF.OptionFieldCondition"
export const OptionFieldDefinitionType = "#TF.OptionFieldDefinition"

export const OptionsConditionDTOType = "#TF.OptionsFieldCondition"
export const OptionsFieldDefinitionType = "#TF.OptionsFieldDefinition"

export const AccountCodesConditionDTOType = "#TF.AccountCodesFieldCondition"
export const AccountCodeFieldDefinitionType = "#TF.AccountCodeFieldDefinition"

export const ActivitiesConditionDTOType = "#TF.ActivitiesFieldCondition"
export const ActivityFieldDefinitionType = "#TF.ActivityFieldDefinition"

export const ApplicationsConditionDTOType = "#TF.ApplicationsFieldCondition"
export const ApplicationFieldDefinitionType = "#TF.ApplicationFieldDefinition"

export const ApplicationVersionsConditionDTOType =
    "#TF.ApplicationVersionsFieldCondition"
export const ApplicationVersionFieldDefinitionType =
    "#TF.ApplicationVersionsFieldDefinition"

export const BillTypesConditionDTOType = "#TF.BillTypesFieldCondition"
export const BillTypeFieldDefinitionType = "#TF.BillTypeFieldDefinition"

export const DisplayTypesConditionDTOType = "#TF.DisplayTypesFieldCondition"
export const DisplayTypeFieldDefinitionType = "#TF.DisplayTypeFieldDefinition"

export const ImpactsConditionDTOType = "#TF.ImpactsFieldCondition"
export const ImpactFieldDefinitionType = "#TF.ImpactFieldDefinition"

export const ProbabilityConditionDTOType = "#TF.ProbabilitiesFieldCondition"
export const ProbabilityFieldDefinitionType = "#TF.ProbabilityFieldDefinition"

export const UserGroupConditionDTOType = "#TF.UserGroupFieldCondition"
export const UserGroupFieldDefinitionType = "#TF.UserGroupFieldDefinition"

export const UserGroupsConditionDTOType = "#TF.UserGroupsFieldCondition"
export const UserGroupsFieldDefinitionType = "#TF.UserGroupsFieldDefinition"

export const UserConditionDTOType = "#TF.UserFieldCondition"
export const UserFieldDefinitionType = "#TF.UserFieldDefinition"

export const UsersConditionDTOType = "#TF.UsersFieldCondition"
export const UsersFieldDefinitionType = "#TF.UsersFieldDefinition"

export const TagsConditionDTOType = "#TF.TagsFieldCondition"
export const TagsFieldDefinitionType = "#TF.TagsFieldDefinition"

export enum FilterSelectorOperatorDTO {
    IsEmpty = "IsEmpty",
    IsOneOf = "IsOneOf",
    ContainsOneOf = "ContainsOneOf",
}

const checkIfApplicable = (condition: Condition): boolean => {
    return condition?.type === ConditionTypes.Relation
}

const getParsedValue = <T extends Type>(
    response: TypeModel[T][],
    type: T,
): ValueWithGroup[] => {
    const mapper = selectorModelMapper(type)
    return response.map((child) => mapper(child))
}

const parseFromDTO = (condition: ConditionDTO): Conditions["Relation"] => {
    let result: Conditions["Relation"] | null = null

    const conditionDTOType =
        FieldDefinitionToConditionDTOType[
            condition.FieldDefinition?.[
                "@type"
            ] as keyof typeof FieldDefinitionToConditionDTOType
        ]

    if (!conditionDTOType) {
        return null
    }
    switch (condition.Operator) {
        case FilterSelectorOperatorDTO.IsEmpty: {
            result = {
                type: ConditionTypes.Relation,
                operator: Operators.Relation.IsEmpty,
                isLocked: condition.IsLocked,
                fieldDefinition: {
                    id: condition?.FieldDefinition?.Id ?? "",
                },
                value: [],
                oDataType: condition?.FieldDefinition?.["@type"] ?? "",
            }
            break
        }
        case FilterSelectorOperatorDTO.ContainsOneOf: {
            result = {
                type: ConditionTypes.Relation,
                operator: Operators.Relation.ContainsOneOf,
                isLocked: condition.IsLocked,
                fieldDefinition: {
                    id: condition?.FieldDefinition?.Id ?? "",
                },
                value: getParsedValue(
                    condition.Value as TypeModel[Type][],
                    getFieldTypeUsingConditionDTOType(
                        conditionDTOType as SelectorConditionDTOType,
                    ),
                ),
                oDataType: condition?.FieldDefinition?.["@type"] ?? "",
            }
            break
        }
        case FilterSelectorOperatorDTO.IsOneOf: {
            result = {
                type: ConditionTypes.Relation,
                operator: Operators.Relation.IsOneOf,
                isLocked: condition.IsLocked,
                fieldDefinition: {
                    id: condition?.FieldDefinition?.Id ?? "",
                },
                value: getParsedValue(
                    condition.Value as TypeModel[Type][],
                    getFieldTypeUsingConditionDTOType(
                        conditionDTOType as SelectorConditionDTOType,
                    ),
                ),
                oDataType: condition?.FieldDefinition?.["@type"] ?? "",
            }
            break
        }
        default:
            break
    }

    return result
}

const parseToDTO = (
    _id: string,
    condition: Condition,
): SelectorConditionDTO | null => {
    if (!condition || !checkIfApplicable(condition)) {
        return null
    }
    condition = condition as Conditions["Relation"]
    let result: SelectorConditionDTO | null = null

    const conditionType = getConditionDTOTypeUsingFieldType(
        condition?.oDataType as keyof TypeModel,
    )

    if (!condition || !conditionType) {
        return null
    }

    switch (condition.operator) {
        case Operators.Relation.IsEmpty: {
            result = {
                "@type": conditionType,
                Operator: FilterSelectorOperatorDTO.IsEmpty,
                IsLocked: condition.isLocked,
                FieldDefinition: { Id: condition.fieldDefinition.id || "" },
                Value: [],
            }
            break
        }
        case Operators.Relation.IsOneOf: {
            result = {
                "@type": conditionType,
                Operator: FilterSelectorOperatorDTO.IsOneOf,
                IsLocked: condition.isLocked,
                FieldDefinition: { Id: condition.fieldDefinition.id || "" },
                Value: condition.value.map((value) => {
                    return { Id: value.id ?? 0 }
                }),
            }
            break
        }
        case Operators.Relation.ContainsOneOf: {
            result = {
                "@type": conditionType,
                Operator: FilterSelectorOperatorDTO.ContainsOneOf,
                IsLocked: condition.isLocked,
                FieldDefinition: { Id: condition.fieldDefinition.id || "" },
                Value: condition.value.map((value) => {
                    return { Id: value.id ?? 0 }
                }),
            }
            break
        }
    }
    return result
}

export const getSelectableConditionType = (
    type: keyof typeof FieldDefinitionTypeToSelectableDTOTypeMap,
): Type => FieldDefinitionTypeToSelectableDTOTypeMap[type]

export const FieldDefinitionTypeToSelectableDTOTypeMap = {
    [OptionFieldDefinitionType]: "TF.OptionItemField",
    [OptionsFieldDefinitionType]: "TF.OptionsItemField",
    [AccountCodeFieldDefinitionType]: "TF.AccountCodeItemField",
    [ActivityFieldDefinitionType]: "TF.ActivityItemField",
    [ApplicationFieldDefinitionType]: "TF.ApplicationItemField",
    [ApplicationVersionFieldDefinitionType]: "TF.ApplicationVersionItemField",
    [BillTypeFieldDefinitionType]: "TF.BillTypeItemField",
    [DisplayTypeFieldDefinitionType]: "TF.DisplayTypeItemField",
    [ImpactFieldDefinitionType]: "TF.ImpactItemField",
    [ProbabilityFieldDefinitionType]: "TF.ProbabilityItemField",
    [UserGroupFieldDefinitionType]: "TF.UserGroupItemField",
    [UserGroupsFieldDefinitionType]: "TF.UserGroupsItemField",
    [UserFieldDefinitionType]: "TF.UserItemField",
    [UsersFieldDefinitionType]: "TF.UsersItemField",
    [TagsFieldDefinitionType]: "TF.TagsItemField",
    // add more here
} as const

const getFieldTypeUsingConditionDTOType = (
    type: SelectorConditionDTOType,
): keyof TypeModel => ConditionDTOToFieldTypeObject[type] as keyof TypeModel

export const ConditionDTOToFieldTypeObject: Record<
    SelectorConditionDTOType,
    Type
> = {
    [OptionConditionDTOType]: "TF.OptionItemField",
    [OptionsConditionDTOType]: "TF.OptionsItemField",
    [AccountCodesConditionDTOType]: "TF.AccountCodeItemField",
    [ActivitiesConditionDTOType]: "TF.ActivityItemField",
    [ApplicationsConditionDTOType]: "TF.ApplicationItemField",
    [ApplicationVersionsConditionDTOType]: "TF.ApplicationVersionItemField",
    [BillTypesConditionDTOType]: "TF.BillTypeItemField",
    [DisplayTypesConditionDTOType]: "TF.DisplayTypeItemField",
    [ImpactsConditionDTOType]: "TF.ImpactItemField",
    [ProbabilityConditionDTOType]: "TF.ProbabilityItemField",
    [UserGroupConditionDTOType]: "TF.UserGroupItemField",
    [UserGroupsConditionDTOType]: "TF.UserGroupsItemField",
    [UserConditionDTOType]: "TF.UserItemField",
    [UsersConditionDTOType]: "TF.UsersItemField",
    [TagsConditionDTOType]: "TF.TagsItemField",
    // add more type here
} as const

const getConditionDTOTypeUsingFieldType = (
    type: Type,
): SelectorConditionDTOType | null => {
    if (type in FIELD_TYPE_TO_CONDITION_DTO) {
        return FIELD_TYPE_TO_CONDITION_DTO[
            type as keyof typeof FIELD_TYPE_TO_CONDITION_DTO
        ] as SelectorConditionDTOType
    }
    return null
}

const FieldDefinitionToConditionDTOType = {
    [OptionFieldDefinitionType]: OptionConditionDTOType,
    [OptionsFieldDefinitionType]: OptionsConditionDTOType,
    [AccountCodeFieldDefinitionType]: AccountCodesConditionDTOType,
    [ActivityFieldDefinitionType]: ActivitiesConditionDTOType,
    [ApplicationFieldDefinitionType]: ApplicationsConditionDTOType,
    [ApplicationVersionFieldDefinitionType]:
        ApplicationVersionsConditionDTOType,
    [BillTypeFieldDefinitionType]: BillTypesConditionDTOType,
    [DisplayTypeFieldDefinitionType]: DisplayTypesConditionDTOType,
    [ImpactFieldDefinitionType]: ImpactsConditionDTOType,
    [ProbabilityFieldDefinitionType]: ProbabilityConditionDTOType,
    [UserGroupFieldDefinitionType]: UserGroupConditionDTOType,
    [UserGroupsFieldDefinitionType]: UserGroupsConditionDTOType,
    [UserFieldDefinitionType]: UserConditionDTOType,
    [UsersFieldDefinitionType]: UsersConditionDTOType,
    [TagsFieldDefinitionType]: TagsConditionDTOType,
} as const

const FIELD_TYPE_TO_CONDITION_DTO = {
    // Filter is multi-select so passing type Options/Users/UserGroups instead of
    // Option/User/UserGroup condition type
    [OptionFieldDefinitionType]: OptionsConditionDTOType,
    [OptionsFieldDefinitionType]: OptionsConditionDTOType,
    [AccountCodeFieldDefinitionType]: AccountCodesConditionDTOType,
    [ActivityFieldDefinitionType]: ActivitiesConditionDTOType,
    [ApplicationFieldDefinitionType]: ApplicationsConditionDTOType,
    [ApplicationVersionFieldDefinitionType]:
        ApplicationVersionsConditionDTOType,
    [BillTypeFieldDefinitionType]: BillTypesConditionDTOType,
    [DisplayTypeFieldDefinitionType]: DisplayTypesConditionDTOType,
    [ImpactFieldDefinitionType]: ImpactsConditionDTOType,
    [ProbabilityFieldDefinitionType]: ProbabilityConditionDTOType,
    [UserGroupFieldDefinitionType]: UserGroupsConditionDTOType,
    [UserGroupsFieldDefinitionType]: UserGroupsConditionDTOType,
    [UserFieldDefinitionType]: UsersConditionDTOType,
    [UsersFieldDefinitionType]: UsersConditionDTOType,
    [TagsFieldDefinitionType]: TagsConditionDTOType,
    // add more type here
}

export const SelectorConditionParser: Omit<FilterParser, "DefinitionDTOType"> =
    {
        Type: ConditionTypes.Relation,
        checkIfApplicable,
        parseFromDTO,
        parseToDTO,
    }
